import { Widgets } from '@common/enums';

/**
 * The bits of a widget entity this module needs.
 * Declared structurally so the helper stays usable from the editor, the SSR renderer and tests alike,
 * none of which share a widget entity type.
 */
export type CatalogBoundWidget = {
    name: string;
    data?: {
        element?: {
            catalogProductId?: number;
            isRemovedFromCatalog?: boolean;
        } | null;
    } | null;
};

/**
 * Ids of the products the CRM catalog no longer has.
 * `null` means the check never happened — the request failed, the tariff expired, nothing was asked.
 * An unknown answer must never be read as "everything is gone": one failed request would otherwise
 * wipe every product card on every published site at once.
 */
export type MissingProductIds = number[] | null;

/** How many ids one `products/all` lookup may carry. Longer pages are split across several requests. */
export const CATALOG_PRODUCT_LOOKUP_LIMIT = 300;

/** The CRM product id a widget is bound to, or `null` when the widget holds a hand-made product. */
export function getCatalogProductId(widget: CatalogBoundWidget | null | undefined): number | null {
    const catalogProductId = widget?.data?.element?.catalogProductId;

    return typeof catalogProductId === 'number' ? catalogProductId : null;
}

/** Whether the widget is a product card bound to a CRM catalog product. */
export function isCatalogBoundProductWidget(widget: CatalogBoundWidget | null | undefined): boolean {
    return widget?.name === Widgets.PRODUCT_CARD && getCatalogProductId(widget) !== null;
}

/** Every CRM product id used on the page, without repeats — two widgets may show the same product. */
export function collectCatalogProductIds(widgets: readonly CatalogBoundWidget[] | null | undefined): number[] {
    const ids = (widgets ?? []).filter(isCatalogBoundProductWidget).map(getCatalogProductId);

    return [...new Set(ids)];
}

/**
 * The ids that were asked for but did not come back.
 *
 * Only meaningful once the lookup succeeded: an absent id means the catalog has no such product,
 * which is exactly what a deletion looks like. Callers must not build this from a failed request —
 * see {@link MissingProductIds}.
 */
export function diffMissingProductIds(
    requestedIds: readonly number[],
    foundProducts: readonly { id: number }[] | null | undefined
): number[] {
    const foundIds = new Set((foundProducts ?? []).map((product) => product.id));

    return requestedIds.filter((id) => !foundIds.has(id));
}

/** Splits the ids into lookup-sized batches so a page with many product cards still resolves. */
export function chunkProductIds(ids: readonly number[], size: number = CATALOG_PRODUCT_LOOKUP_LIMIT): number[][] {
    const chunks: number[][] = [];

    for (let index = 0; index < ids.length; index += size) {
        chunks.push(ids.slice(index, index + size));
    }

    return chunks;
}

/**
 * Serializes page structure for saving, leaving out `isRemovedFromCatalog`.
 * The flag describes the catalog at load time and is recomputed on every load, so a stored copy
 * could only ever be stale.
 */
export function stringifyPageStructure(value: unknown): string {
    return JSON.stringify(value, (key, item) => (key === 'isRemovedFromCatalog' ? undefined : item));
}
